import {Injectable} from '@angular/core';
import {MeterTypeEnum} from "../models/meter-type.enum";
import {AlertService} from "./alert.service";
import {ShareService} from "./share.service";
import {BuildingModel} from "../models/data-models/building-model";
import {GeneralGatewayModel} from "../models/data-models/general-gateway-model";
import {GeneralMeasurementModel} from "../models/data-models/general-measurement-model";
import {GeneralPrePhotosModel} from "../models/data-models/general-pre-photos-model";
import {GeneralSendingModel} from "../models/data-models/general-sending-model";
import {HeatingCheckModel} from "../models/data-models/heating-check-model";
import {HeatingInformationModel} from "../models/data-models/heating-information-model";
import {PhotosModel} from "../models/data-models/photos-model";
import {SensorsCounterModel} from "../models/data-models/sensors-counter-model";
import {SensorsOtherModel} from "../models/data-models/sensors-other-model";
import {SensorsOutsideTempModel} from "../models/data-models/sensors-outside-temp-model";
import {SensorsPumpModel} from "../models/data-models/sensors-pump-model";
import {SensorsTemperatureModel} from "../models/data-models/sensors-temperature-model";
import {MeterSensorModel} from "../models/meter-sensor-model";
import {OtherSensorModel} from "../models/other-sensor.model";
import {PressureSensorModel} from "../models/perssure-sensor-model";
import {PfcSensorModel} from "../models/pfc-sensor.model";
import {PumpSensorHelperModel} from "../models/pump-sensor-helper-model";
import {TemperatureSensorHelperModel} from "../models/temperature-sensor-helper-model";
import {UvsModuleModel} from "../models/uvs-module.model";
import {GatewayType, isReadOnlyModuleType} from "../interfaces/juconnGateway.response";
import {Customer} from "../models/customer-model";
import {DataService} from "./data.service";

@Injectable({
  providedIn: 'root'
})
export class ValidationService {

  constructor(
    private alertService: AlertService,
    private shareService: ShareService,
    private dataService: DataService
  ) {
  }

  public async isSelectedSiteStillValid(): Promise<boolean> {
    const customerOptions = (await this.dataService.getCustomersWithAvailableSites()) as Customer[];
    const selectedCustomers = customerOptions.filter(customer => customer.customerId === this.shareService.generalGateway.selectedCustomer.customerId);
    if (selectedCustomers.length !== 1) {
      if (this.shareService.generalGateway?.additionalGateway) {
        this.alertService.error('An dieser Adresse ist keiner Gateway installiert, ist nicht erlaubt eine zusätzliche Gateway zu installieren.');
      } else {
        this.alertService.error('An dieser Adresse ist bereits ein klassisches Gateway installiert, es sind nur weitere zusätzliche Gateways erlaubt');
      }
      return false;
    } else {
      const customer = selectedCustomers[0];
      if (this.shareService.generalGateway?.additionalGateway) {
        const selectedSites = customer.installedSites.filter(site => site.id === this.shareService.generalGateway?.selectedSite?.id);
        const result = selectedSites.length === 1;
        if (!result) {
          this.alertService.error('An dieser Adresse ist keiner Gateway installiert, ist nicht erlaubt eine zusätzliche Gateway zu installieren.');
        }
        return result;
      } else {
        const selectedSites = customer.plannedSites.filter(site => site.id === this.shareService.generalGateway?.selectedSite?.id);
        const result = selectedSites.length === 1;
        if (!result) {
          this.alertService.error('An dieser Adresse ist bereits ein klassisches Gateway installiert, es sind nur weitere zusätzliche Gateways erlaubt');
        }
        return result;
      }
    }
  }

  public isCollectedDataValid(): boolean {
    // gather new models from ShareService instead of the old collectedData object
    const generalGateway = this.shareService.generalGateway;
    const building = this.shareService.building;
    const photos = this.shareService.photos;
    const prePhotos = this.shareService.generalPrePhotos;
    const generalMeasurement = this.shareService.generalMeasurement;
    const heatingCheck = this.shareService.heatingCheck;
    const heatingInformation = this.shareService.heatingInformation;
    const sensorsTemperature = this.shareService.sensorsTemperature;
    const sensorsPump = this.shareService.sensorsPump;
    const sensorsCounter = this.shareService.sensorsCounter;
    const sensorsOther = this.shareService.sensorsOther;

    const gatewayId = this.shareService.getGatewayId();
    const simIp = this.shareService.simIp;

    console.log('collected data validation', {
      generalGateway,
      building,
      photos,
      heatingInformation,
      heatingCheck,
      sensorsTemperature,
      sensorsPump,
      sensorsCounter,
      sensorsOther
    });

    const errors: string[] = [];
    const heatingTypes: string[] = [
      'GAS', 'OIL', 'WOOD', 'PELLET', 'ELECTRIC', 'HEAT_PUMP', 'BHKW', 'SOLAR_THERMAL', 'DISTRICT', 'INFRARED', 'SUB_DISTRIBUTION'
    ];

    // basic presence check: if nothing is set at all
    if (!generalGateway && !building && !photos && !heatingInformation && !sensorsTemperature) {
      errors.push('Es liegen keine Daten vor. Bitte versuchen Sie das gesamte Formular noch einmal von Anfang an auszufüllen.');
    }

    if (!this.hasText(gatewayId)) {
      errors.push('Es ist keine Gateway-Seriennummer eingetragen. Bitte wählen Sie ein Gateway aus.');
    }

    // gatewayStyle is not part of new small models, skip this specific check or rely on selectedCustomer presence
    if (!generalGateway?.selectedCustomer) {
      errors.push('Es ist kein Hersteller / Kunde eingetragen. Bitte überprüfen Sie dies auf der Registerkarte „Allgemein“.');
    }

    if (generalGateway?.additionalGateway === undefined || generalGateway?.additionalGateway === null) {
      errors.push('Dir fehlt im Reiter „Allgemein“ die Festlegung, ob es sich um ein klassisches oder ein zusätzliches Gateway handelt.');
    }

    if (!generalGateway?.selectedCustomer?.customerId) {
      errors.push('Es ist kein Kunde eingetragen. Bitte überprüfen Sie dies auf der Registerkarte „Allgemein“.');
    }

    let isAdditionalGatewayAndSiteValid = true;
    if (!generalGateway?.additionalGateway) {
      const site = generalGateway?.selectedSite;
      isAdditionalGatewayAndSiteValid = this.hasText(site?.city) && this.hasText(site?.street) && this.hasText(site?.streetNumber) && this.hasText(site?.postCode);
      if (!isAdditionalGatewayAndSiteValid) {
        errors.push('Auf Ihrer Site fehlt eine Adresse. Bitte überprüfen Sie diese im Reiter „Allgemein“.');
      }
    } else {
      if (!generalGateway?.selectedSite?.id) {
        errors.push('Offenbar haben Sie keinen Liegenschaft ausgewählt. Bitte überprüfen Sie dies auf der Registerkarte „Allgemein“.');
      }
    }

    if (!this.hasText(simIp)) {
      errors.push('Einige Gateway-Daten fehlen. Bitte versuchen Sie, erneut ein Signal zu empfangen oder wählen Sie ein anderes Gateway aus.');
    }

    const site = generalGateway?.selectedSite;
    if (!this.hasText(site?.city)) {
      errors.push('In der Adresse Ihrer Liegenschaft fehlt die Stadt. Bitte überprüfen Sie dies auf der Registerkarte „Allgemein“.');
    }
    if (!this.hasText(site?.postCode)) {
      errors.push('In der Adresse Ihrer Liegenschaft fehlt die Postleitzahl. Bitte überprüfen Sie dies auf der Registerkarte „Allgemein“.');
    }
    if (!this.hasText(site?.street)) {
      errors.push('In der Adresse Ihrer Liegenschaft fehlt die Straßenangabe. Bitte überprüfen Sie dies im Reiter „Allgemein“.');
    }
    if (!this.hasText(site?.streetNumber)) {
      errors.push('In der Adresse Ihrer Liegenschaft fehlt die Hausnummer. Bitte überprüfen Sie dies im Reiter „Allgemein“.');
    }

    if (!this.hasText(building?.heatingLocation)) {
      errors.push('Ihnen fehlt der Standort Heizanlage. Bitte prüfen Sie diesen im Reiter „Allgemein“.');
    }

    if (!this.hasText(photos?.installationBoxImage)) {
      errors.push('Ihnen fehlt das Foto Installationsbox. Bitte prüfen Sie dieses im des Fotos-Tab.');
    }
    if (!this.hasText(photos?.antennaImage)) {
      errors.push('Ihnen fehlt das Foto Antenne. Bitte prüfen Sie dieses im des Fotos-Tab.');
    }
    if (!this.hasText(photos?.schemeImage)) {
      errors.push('Ihnen fehlt das Foto Heizungsschema. Bitte prüfen Sie dieses im des Fotos-Tab.');
    }

    if (!this.hasText(building?.contactPersonName)) {
      errors.push('Der Name Ansprechpartner fehlt. Bitte prüfen Sie diesen im Reiter „Allgemein“.');
    }

    const heatings = heatingInformation?.heatings || [];
    if ((!generalGateway?.additionalGateway || heatings.length !== 0) && heatings.length !== 1) {
      if (heatings.length < 1) {
        errors.push('Es ist mindestens eine Heizung erforderlich. Bitte überprüfen Sie dies im Reiter Heizung.');
      }
      if (!this.hasText(heatingInformation?.heatingControlType)) {
        errors.push('Es ist keine Steuerung ausgewählt. Bitte überprüfen Sie dies im Reiter Heizung.');
      }
    }

    if (heatings.length > 0) {
      for (let item of heatings) {
        if (!this.hasText(item.heatingType)) {
          errors.push('Für jede Heizung muss eine Heizungsart angegeben werden. Diese können Sie im Reiter „Heizung“ überprüfen.');
        }
        if (!heatingTypes.find(heatingType => heatingType === item.heatingType)) {
          errors.push('Ungültige Heizungstyp. Bitte prüfen Sie dies im Allgemein-Tab.');
        }
      }
    }

    if (!(heatings.length === 1 && "SUB_DISTRIBUTION" === heatings[0]?.heatingType && heatingInformation?.heatingControlType === 'BOILERS')) {
      if (!heatingCheck) {
        errors.push('Heizungschecks wurden nicht ausgefüllt. Bitte prüfen Sie dies im Reiter Heizung.');
      } else {
        if (!heatingCheck.averageRoomTemperature) {
          errors.push('Die durchschnittliche Wohnraumtemperatur ist bei den Heizungschecks nicht ausgefüllt. Bitte überprüfen Sie sie auf der Registerkarte „Heizung“.');
        }
        if (heatingCheck.emergencyStopAvailable === undefined || heatingCheck.emergencyStopAvailable === null) {
          errors.push('Die Notausschalter vorhanden wird bei den Heizungschecks nicht ausgefüllt. Bitte überprüfen Sie sie auf der Registerkarte „Heizung“.');
        }
        if (heatingCheck.insulatedPipes === undefined || heatingCheck.insulatedPipes === null) {
          errors.push('Die Rohrleitungen gedämmt wird bei den Heizungschecks nicht ausgefüllt. Bitte überprüfen Sie sie auf der Registerkarte „Heizung“.');
        }
        if (heatingCheck.insulatingCaps === undefined || heatingCheck.insulatingCaps === null) {
          errors.push('Die Dämmkappen auf den Armaturen wird bei den Heizungschecks nicht ausgefüllt. Bitte überprüfen Sie sie auf der Registerkarte „Heizung“.');
        }
        if (heatingCheck.outdoorTemperatureAvailable === undefined || heatingCheck.outdoorTemperatureAvailable === null) {
          errors.push('Der Auẞentemperaturfühler ist bei den Heizungschecks nicht eingetragen.  Bitte überprüfen Sie sie auf der Registerkarte „Heizung“.');
        }
        if (heatingCheck.summerWinterOperationAvailable === undefined || heatingCheck.summerWinterOperationAvailable === null) {
          errors.push('“Sommer- / Winterbetrieb möglich” ist bei den Heizungschecks nicht ausgefüllt. Bitte überprüfen Sie sie auf der Registerkarte „Heizung“.');
        }
      }
    }

    // temperature sensors: use helper list if present
    const tempHelpers = sensorsTemperature?.temperatureSensorsHelperList || [];
    if (tempHelpers.length > 0) {
      for (let item of tempHelpers) {
        if (!item.sensorId) {
          errors.push('Die Sensor-ID für einen Temperatursensor ist nicht ausgefüllt. Bitte überprüfen Sie sie im Temperatur-Tab des Sensoren-Tabs.');
        }
        if (!this.hasText(item.name)) {
          errors.push('Der Name für einen Temperatursensor ist nicht ausgefüllt. Bitte überprüfen Sie sie im Temperatur-Tab des Sensoren-Tabs.');
        }
        if (!item?.photo) {
          errors.push('Das Bild für einen Temperatursensor ist nicht ausgefüllt. Bitte überprüfen Sie sie im Temperatur-Tab des Sensoren-Tabs.');
        }
        // value checks are device readings and are not available in helper model -> skip
      }
    }

    const pumpSensors = sensorsPump?.pumpSensors || [];
    if (pumpSensors.length > 0) {
      for (let item of pumpSensors) {
        if (!item.sensorId) {
          errors.push('Die Sensor-ID für einen Pumpensensor ist nicht ausgefüllt. Bitte überprüfen Sie sie im Reiter „Pumpen“ des Sensoren-Reiters.');
        }
        if (!this.hasText(item.name)) {
          errors.push('Der Name für einen Pumpensensor ist nicht ausgefüllt. Bitte überprüfen Sie sie im Reiter „Pumpen“ des Sensoren-Reiters.');
        }
        if (!item.minValue) {
          errors.push('Der Grenzwert für einen Pumpensensor ist nicht ausgefüllt. Bitte überprüfen Sie sie im Reiter „Pumpen“ des Sensoren-Reiters.');
        }
        if (!item.sensorType) {
          errors.push('Das Sensorart für einen Pumpensensor ist nicht ausgefüllt. Bitte überprüfen Sie sie im Reiter „Pumpen“ des Sensoren-Reiters.');
        }
        if (!item?.image) {
          errors.push('Das Bild für einen Pumpensensor ist nicht ausgefüllt. Bitte überprüfen Sie sie im Reiter „Pumpen“ des Sensoren-Reiters.');
        }
        if (!this.hasText(item.pumpBrand)) {
          errors.push('Das Fabrikat für einen Pumpensensor ist nicht ausgefüllt. Bitte überprüfen Sie sie im Reiter „Pumpen“ des Sensoren-Reiters.');
        }
        if (!this.hasText(item.pumpType)) {
          errors.push('Das Typ für einen Pumpensensor ist nicht ausgefüllt. Bitte überprüfen Sie sie im Reiter „Pumpen“ des Sensoren-Reiters.');
        }
        if (item.pumpElectronicallyControlled === undefined || item.pumpElectronicallyControlled === null) {
          errors.push('Der Elektronisch geregelt für einen Pumpensensor ist nicht ausgefüllt. Bitte überprüfen Sie sie im Reiter „Pumpen“ des Sensoren-Reiters.');
        }
      }
    }

    const meterSensors = sensorsCounter?.counterSensors || [];
    if (meterSensors.length > 0) {
      for (let item of meterSensors) {
        if (!item.meterType) {
          errors.push('Die Zählerart für einen Zählersensor ist nicht ausgefüllt. Bitte überprüfen Sie sie im Reiter „Zähler“ unter dem Reiter „Sensoren“.');
        }
        if (!this.hasText(item.image)) {
          errors.push('Das Bild für einen Zählersensor ist nicht ausgefüllt. Bitte überprüfen Sie es im Reiter „Zähler“ unter dem Reiter „Sensoren“.');
        }
        if (!item.module && item.module !== 0) {
          errors.push('Das Modul für einen Zählersensor wurde nicht angegeben. Bitte überprüfen Sie es im Reiter „Zähler“ im Bereich „Sensoren“.');
        }
        if (!item.sensorId) {
          errors.push('Die ID für einen Zählersensor wurde nicht angegeben. Bitte überprüfen Sie sie im Reiter „Zähler" im Bereich "Sensoren".');
        }
        if (!this.hasText(item.name)) {
          errors.push('Der Name für einen Zählersensor ist nicht ausgefüllt. Bitte überprüfen Sie es im Reiter „Zähler“ unter dem Reiter „Sensoren“.');
        }
        if (!this.hasText(item.seriesNumber)) {
          errors.push('Das Seriennummer für einen Zählersensor ist nicht ausgefüllt. Bitte überprüfen Sie es im Reiter „Zähler“ unter dem Reiter „Sensoren“.');
        }
        if (!this.hasText(item.manufacturer)) {
          errors.push('Der Hersteller für einen Zählersensor ist nicht ausgefüllt. Bitte überprüfen Sie es im Reiter „Zähler“ unter dem Reiter „Sensoren“.');
        }
        if (item.meterType == MeterTypeEnum.IR) {
          if (!this.hasText(item.irType)) {
            errors.push('Der IR Typ für einen Zählersensor wurde nicht angegeben. Bitte überprüfen Sie ihn im Reiter „Zähler“ im Bereich „Sensoren“.');
          }
          if (!item.value) {
            errors.push('Der ausgelesene Wert für einen Zählersensor wurde nicht angegeben. Bitte überprüfen Sie ihn im Reiter „Zähler“ im Bereich „Sensoren“.');
          }
        } else if (item.meterType === MeterTypeEnum.Impulse) {
          if (!item.impulse?.stepInUnit) {
            errors.push('Das Impuls pro Einheit für einen Zählersensor ist nicht ausgefüllt. Bitte überprüfen Sie es im Reiter „Zähler“ unter dem Reiter „Sensoren“.');
          }
          if (!item.impulse?.initOffset) {
            errors.push('Das Aktuell für einen Zählersensor ist nicht ausgefüllt. Bitte überprüfen Sie es im Reiter „Zähler“ unter dem Reiter „Sensoren“.');
          }
        } else {
          errors.push('Die ausgewählte Zählerart ist unbekannt. Bitte überprüfen Sie die Auswahl im Reiter „Zähler“ unter „Sensoren".');
        }
      }
    }

    if (sensorsOther?.otherSensors?.length > 0) {
      sensorsOther.otherSensors.forEach(item => {
        if (!item.uvsModuleId) {
          errors.push('Die Modul-ID für einen Sonstige-Sensor ist nicht ausgefüllt. Bitte überprüfen Sie dies im Reiter „Sonstiges“ des Sensoren-Tabs.');
        }
        if (!item.sensorType) {
          errors.push('Der Sensortyp für einen Sonstige-Sensor ist nicht ausgefüllt. Bitte überprüfen Sie dies im Reiter „Sonstiges“ des Sensoren-Tabs.');
        }
      });
    }

    if (sensorsOther?.pressureSensors?.length > 0) {
      sensorsOther.pressureSensors.forEach(item => {
        if (!item.sensorId) {
          errors.push('Die Sensor-ID für einen Drucksensor ist nicht ausgefüllt. Bitte überprüfen Sie dies im Reiter „Sonstiges“ des Sensoren-Tabs.');
        }
        if (item.lowerLimit === undefined || item.lowerLimit === null) {
          errors.push('Die Untergrenze für einen Drucksensor ist nicht ausgefüllt. Bitte überprüfen Sie dies im Reiter „Sonstiges“ des Sensoren-Tabs.');
        }
        if (!item.upperLimit) {
          errors.push('Die Obergrenze für einen Drucksensor ist nicht ausgefüllt. Bitte überprüfen Sie dies im Reiter „Sonstiges“ des Sensoren-Tabs.');
        }
        if (!item?.image) {
          errors.push('Das Bild für einen Drucksensor ist nicht ausgefüllt. Bitte überprüfen Sie dies im Reiter „Sonstiges“ des Sensoren-Tabs.');
        }
      });
    }

    if (sensorsOther?.pfcSensors?.length > 0) {
      sensorsOther.pfcSensors.forEach(item => {
        if (!item.uvsModuleId) {
          errors.push('Die Module-ID für einen PFC-sensor ist nicht ausgefüllt. Bitte überprüfen Sie dies im Reiter „Sonstiges“ des Sensoren-Tabs.');
        }
        if (!item.name) {
          errors.push('Die Sensor für einen PFC-sensor ist nicht ausgefüllt. Bitte überprüfen Sie dies im Reiter „Sonstiges“ des Sensoren-Tabs.');
        }
        if (!item?.image) {
          errors.push('Das Bild für einen  PFC-sensor ist nicht ausgefüllt. Bitte überprüfen Sie dies im Reiter „Sonstiges“ des Sensoren-Tabs.');
        }
      });
      const uvsModules = generalGateway?.uvsModules || [];
      uvsModules.forEach(module => {
        const pfcSensorCount = (sensorsOther?.pfcSensors || []).filter(sensor => sensor.uvsModuleId === module.address).length;
        if (pfcSensorCount > 4) {
          errors.push('An Modul ' + module.address + ' können höchstens 4 PFC-Sensoren angeschlossen werden.');
        }
      });
    }

    // pre / post install photos checks
    if ((sensorsOther?.pfcSensors?.length || 0) === 0) {
      if ((prePhotos?.preInstallHeatingImages?.length || 0) === 0) {
        errors.push('Keine Foto nach Installation der Steuerung vorhanden. Bitte überprüfen Sie dies im Allgemein-Tab.');
      }
    }

    if ((photos?.postInstallHeatingImages?.length || 0) === 0 || !photos?.postInstallHeatingImages?.[0]?.image) {
      errors.push('Keine Foto Steuerung. Bitte überprüfen Sie dies im Gateway-Tab.');
    }

    if ((photos?.postInstallHeatingImages?.length || 0) < (prePhotos?.preInstallHeatingImages?.length || 0)) {
      errors.push('Nicht genügend Fotos nach Installation der Steuerung vorhanden. Bitte überprüfen Sie dies in den Fotos-Tab.');
    }

    if ((prePhotos?.preInstallBoilerRoomImages?.length || 0) === 0) {
      errors.push('Keine Fotos vom Heizkeller vor der Installation vorhanden. Bitte überprüfen Sie dies im Allgemein-Tab.');
    }

    if (!photos?.postInstallBoilerRoomImages?.length || !photos?.postInstallBoilerRoomImages?.[0]?.image || !photos?.postInstallBoilerRoomImages?.[1]?.image || !photos?.postInstallBoilerRoomImages?.[2]?.image) {
      errors.push('Keine Fotos vom Heizkeller nach der Installation vorhanden. Bitte überprüfen Sie dies im Fotos-Tab.');
    }

    if (!photos?.postInstallBoilerRoomImages?.[0]?.image || !photos?.postInstallBoilerRoomImages?.[1]?.image || !photos?.postInstallBoilerRoomImages?.[2]?.image) {
      errors.push('Nicht genügend Fotos vom Heizkeller nach der Installation vorhanden. Bitte überprüfen Sie dies im Fotos-Tab.');
    }

    console.log('collected data validation errors', errors);
    if (errors.length > 0) {
      errors.forEach(error => this.alertService.error(error));
      return false;
    } else {
      return true;
    }
  }

  private hasText(value: string | undefined | null) {
    return value && value.trim().length > 0;
  }

  public isAllDataValid(): boolean {
    return this.isBuildingValid(this.shareService.building) &&
      this.isGeneralValid(this.shareService.generalGateway, this.shareService.generalMeasurement, this.shareService.generalPrePhotos, this.shareService.generalSending) &&
      this.isHeatingValid(this.shareService.heatingCheck, this.shareService.heatingInformation) &&
      this.isPhotosValid(this.shareService.photos, this.shareService.generalPrePhotos?.preInstallHeatingImages.length) &&
      this.isSensorsValid(this.shareService.generalGateway?.uvsModules, this.shareService.sensorsCounter, this.shareService.sensorsOther, this.shareService.sensorsOutsideTemp, this.shareService.sensorsPump, this.shareService.sensorsTemperature) &&
      this.userSensorsDefinedOrOnlyOtmModules(this.shareService.generalGateway?.uvsModules, this.shareService.sensorsCounter, this.shareService.sensorsOther, this.shareService.sensorsPump, this.shareService.sensorsTemperature);

  }

  public isBuildingValid(building: BuildingModel): boolean {
    return !!building?.heatingLocation && !!building?.contactPersonName;
  }

  public isGeneralValid(gateway: GeneralGatewayModel, measurement: GeneralMeasurementModel, prePhotos: GeneralPrePhotosModel, sending: GeneralSendingModel): boolean {
    return this.isGeneralGatewayValid(gateway) &&
      this.isGeneralMeasurementValid(measurement) &&
      this.isGeneralPrePhotosValid(prePhotos) &&
      this.isGeneralSendingValid(sending);
  }

  public isGeneralGatewayValid(model: GeneralGatewayModel): boolean {
    return !!model?.selectedCustomer &&
      !!model?.selectedSite &&
      !!model?.uvsModules &&
      model.uvsModules.length > 0;
  }

  public isGeneralMeasurementValid(model: GeneralMeasurementModel): boolean {
    const m = model?.installationMaterial;
    return this.isNotNullOrUndefined(m?.nymCable) &&
      this.isNotNullOrUndefined(m?.controlCable) &&
      this.isNotNullOrUndefined(m?.shieldedCable) &&
      this.isNotNullOrUndefined(m?.emptyConduit) &&
      this.isNotNullOrUndefined(m?.junctionBoxes) &&
      this.isNotNullOrUndefined(m?.countTemperatureSensors) &&
      this.isNotNullOrUndefined(m?.countElectricityClamps) &&
      this.isNotNullOrUndefined(m?.countMeters) &&
      this.isNotNullOrUndefined(m?.countModules);
  }

  public isGeneralPrePhotosValid(model: GeneralPrePhotosModel): boolean {
    const boilerRoomValid = model?.preInstallBoilerRoomImages?.length >= 3 &&
      model.preInstallBoilerRoomImages.every(img => !!img.image);
    const heatingValid = model?.preInstallHeatingImages?.length > 0 &&
      model.preInstallHeatingImages.every(
        (img, index) => !!img.image || (index === (model.preInstallHeatingImages.length - 1) && index !== 0)
      );
    return boilerRoomValid && heatingValid;
  }

  public isGeneralSendingValid(model: GeneralSendingModel): boolean {
    return model.nonInstallableSensors.length === 0 || model.nonInstallableSensors.every(s => !!s.type && !!s.sensorName && !!s.reason)
  }

  public isHeatingValid(check: HeatingCheckModel, information: HeatingInformationModel): boolean {
    return this.isHeatingCheckValid(check) &&
      this.isHeatingInformationValid(information);
  }

  public isHeatingCheckValid(model: HeatingCheckModel): boolean {
    return this.isNotNullOrUndefined(model?.averageRoomTemperature) &&
      this.isNotNullOrUndefined(model?.outdoorTemperatureAvailable) &&
      this.isNotNullOrUndefined(model?.summerWinterOperationAvailable) &&
      this.isNotNullOrUndefined(model?.emergencyStopAvailable) &&
      this.isNotNullOrUndefined(model?.insulatedPipes) &&
      this.isNotNullOrUndefined(model?.insulatingCaps);
  }

  public isHeatingInformationValid(model: HeatingInformationModel): boolean {
    return this.isNotNullOrUndefined(model?.heatingRoomLength) &&
      this.isNotNullOrUndefined(model?.heatingRoomWidth) &&
      this.isNotNullOrUndefined(model?.heatingShutdownPossible) &&
      this.isNotNullOrUndefined(model?.heatingSpaceAvailable) &&
      (
        (
          model.heatings?.length === 1 &&
          model.heatings[0].heatingType === 'SUB_DISTRIBUTION' &&
          !!model.heatingControlType
        ) ||
        model.heatings?.every(h => h.heatingType && h.manufacturer && h.heatingTypeImage && h.heatingSystemImage)
      );
  }

  public isPhotosValid(model: PhotosModel, preInstallHeatingImagesCount: number): boolean {
    return !!model?.installationBoxImage &&
      !!model?.antennaImage &&
      !!model?.schemeImage &&
      model?.postInstallBoilerRoomImages?.length >= 3 &&
      model.postInstallBoilerRoomImages.every(img => !!img.image) &&
      model?.postInstallHeatingImages?.length > 0 &&
      model.postInstallHeatingImages.every(
        (img, index) => !!img.image || index >= (preInstallHeatingImagesCount - 1)
      );
  }

  public isSensorsValid(modules: UvsModuleModel[], counter: SensorsCounterModel, other: SensorsOtherModel, outTemp: SensorsOutsideTempModel, pump: SensorsPumpModel, temperature: SensorsTemperatureModel): boolean {
    return this.isSensorsCounterValid(counter) &&
      this.isSensorsOtherValid(other) &&
      this.isSensorsControlValid(outTemp) &&
      this.isSensorsPumpValid(pump) &&
      this.isSensorsTemperatureValid(temperature) &&
      this.userSensorsDefinedOrOnlyOtmModules(modules, counter, other, pump, temperature);
  }

  public isSensorsCounterValid(model: SensorsCounterModel): boolean {
    return model?.counterSensors?.every(sensor => this.isCounterSensorValid(sensor)) ?? true;
  }

  private isCounterSensorValid(sensor: MeterSensorModel): boolean {
    if (!sensor) {
      return false;
    }
    const hasBaseFields = !!sensor.image && !!sensor.meterType && !!sensor.module && !!sensor.sensorId && !!sensor.name;
    if (!hasBaseFields) {
      return false;
    }
    if (sensor.meterType === MeterTypeEnum.IR) {
      return !!sensor.irType &&
        !!sensor.seriesNumber &&
        !!sensor.manufacturer &&
        (this.isNotNullOrUndefined(sensor.value) || !!sensor.smlValue.length);
    }
    if (sensor.meterType === MeterTypeEnum.Impulse) {
      return !!sensor.seriesNumber &&
        !!sensor.manufacturer &&
        !!sensor.impulse &&
        this.isNotNullOrUndefined(sensor.impulse.stepInUnit) &&
        this.isNotNullOrUndefined(sensor.impulse.initOffset);
    }
    return false;
  }

  public isSensorsOtherValid(model: SensorsOtherModel): boolean {
    return model?.otherSensors?.every(s => this.isOtherSensorValid(s)) &&
      model?.pressureSensors?.every(s => this.isPressureSensorValid(s)) &&
      model?.pfcSensors?.every(s => this.isPfcSensorValid(s));
  }

  private isOtherSensorValid(sensor: OtherSensorModel): boolean {
    return !!sensor &&
      this.isNotNullOrUndefined(sensor.uvsModuleId) &&
      this.isNotNullOrUndefined(sensor.sensorType) &&
      !!sensor.image;
  }

  private isPressureSensorValid(sensor: PressureSensorModel): boolean {
    if (!sensor) {
      return false;
    }
    const hasRequiredFields = !!sensor.sensorId &&
      !!sensor.image &&
      this.isNotNullOrUndefined(sensor.lowerLimit) &&
      this.isNotNullOrUndefined(sensor.upperLimit);
    if (!hasRequiredFields) {
      return false;
    }
    return sensor.lowerLimit <= sensor.upperLimit;
  }

  private isPfcSensorValid(sensor: PfcSensorModel): boolean {
    return !!sensor &&
      !!sensor.sensorId &&
      !!sensor.image;
  }

  public isSensorsControlValid(model: SensorsOutsideTempModel): boolean {
    if (model?.otmModules?.length === 0) {
      return model?.directControl?.enabled === false || (model?.directControl?.enabled === true && !!model?.directControl?.type);
    }
    return model?.otmModules?.every(module => this.isOtmModuleValid(module));
  }

  private isOtmModuleValid(module: UvsModuleModel): boolean {
    return !!module && !!module.sensorTypeName;
  }

  public isSensorsPumpValid(model: SensorsPumpModel): boolean {
    return model?.pumpSensors?.every(sensor => this.isPumpSensorValid(sensor)) ?? true;
  }

  private isPumpSensorValid(sensor: PumpSensorHelperModel): boolean {
    return !!sensor &&
      !!sensor.sensorId &&
      !!sensor.name &&
      !!sensor.image &&
      this.isNotNullOrUndefined(sensor.minValue) &&
      this.isNotNullOrUndefined(sensor.sensorType) &&
      !!sensor.pumpBrand &&
      !!sensor.pumpType;
  }

  public isSensorsTemperatureValid(model: SensorsTemperatureModel): boolean {
    return model?.temperatureSensorsHelperList?.every(sensor => this.isTemperatureSensorValid(sensor)) ?? true;
  }

  private isTemperatureSensorValid(sensor: TemperatureSensorHelperModel): boolean {
    if (!sensor) {
      return false;
    }
    if (!(sensor.sensorId && sensor.name && sensor.photo)) {
      return false;
    }
    if (sensor.pairMode === 'PAIR' || sensor.pairMode === 'TRIO') {
      if (!sensor.sensor2Id || !sensor.name2 || !sensor.photo2) {
        return false;
      }
    }
    if (sensor.pairMode === 'TRIO') {
      return !!sensor.sensor3Id && !!sensor.name3 && !!sensor.photo3;
    }
    return true;
  }

  public userSensorsDefinedOrOnlyOtmModules(modules: UvsModuleModel[], counter: SensorsCounterModel, other: SensorsOtherModel, pump: SensorsPumpModel, temperature: SensorsTemperatureModel): boolean {
    modules = modules?.filter(module => !isReadOnlyModuleType(module.uvsType));
    const onlyOtmModules = modules ? modules.filter(module => module.uvsType === GatewayType.OTM01 || module.uvsType === GatewayType.OTM02 || module.uvsType === GatewayType.OTM03).length === modules.length : false;
    if (onlyOtmModules) {
      return true;
    }
    for (let module of modules || []) {
      if (module.uvsType === GatewayType.OTM01 || module.uvsType === GatewayType.OTM02 || module.uvsType === GatewayType.OTM03) {
        if ((module.measuredValues.sensorResistance === null || module.measuredValues.sensorResistance === undefined) ||
          (
            (module.measuredValues.outsideTemperature === null || module.measuredValues.outsideTemperature === undefined) &&
            ((module.measuredValues.temperatureDisplayedByHeatingSystem === null || module.measuredValues.temperatureDisplayedByHeatingSystem === undefined))
          )
        ) {
          return false;
        }
      } else {
        if (!counter.counterSensors.some(s => s.module === module.address) &&
          !pump.pumpSensors.some(s => s.sensorId?.startsWith(module.address.toString()) && !!s.sensorId && !!s.image && !!s.name && !!s.minValue && !!s.sensorType && !!s.pumpBrand && !!s.pumpType) &&
          !temperature.temperatureSensorsHelperList.some(s => s.sensorId?.startsWith(module.address.toString()) && !!s.sensorId && !!s.photo && !!s.name) &&
          !other.pressureSensors?.some(s => s.sensorId?.startsWith(module.address.toString())) &&
          !other.otherSensors?.some(s => s.uvsModuleId === module.address) &&
          !other.pfcSensors?.some(s => s.uvsModuleId === module.address)) {
          return false;
        }
      }
    }
    return true;
  }

  private isNotNullOrUndefined(value: any): boolean {
    return value !== null && value !== undefined;
  }
}
